import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { PaperIconComponent } from '@ricoh-paper/angular';
import { PlatformCustomerService } from '../../../../core/services/platform-customer.service';
import { AnalyticsService } from '../../../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../../../core/analytics/events';
import {
  SWITCHER_SEARCH_THRESHOLD,
  filterCustomers,
  shouldShowSearch,
} from '../org-switcher/org-switcher-filter.util';

/**
 * How long the workspace filter sits still before it reports a search.
 *
 * It is a debounce, not a throttle: one event per SEARCH, not one per
 * keystroke, so `query_length` describes what the user actually looked for
 * rather than every prefix of it.
 */
const WORKSPACE_SEARCH_DEBOUNCE_MS = 400;

/**
 * ws-workspace-switcher — "Switch workspace", the only entry point for
 * changing customer, inside Paper's profile panel (`slot="extra"`).
 *
 * Lifted out of the old `im-profile-panel` when the shell moved to
 * `paper-profile-panel` (One Paper A4). Paper's panel draws identity, the
 * theme and the accent; this keeps the three behaviours that only travel
 * together:
 *   1. the search box past the disclosure threshold — via the shared
 *      `shouldShowSearch` / `filterCustomers`, not a second match rule;
 *   2. the PII contract on the search event (length + counts, never the
 *      text) — `onQueryChange` below;
 *   3. the customer-scoped `localStorage` purge — inside
 *      `PlatformCustomerService.switchTo()`.
 *
 * The rows take the panel's menu-row look (Paper's tokens: a 36px row, the
 * hover plane, the inset focus ring), so the block reads as part of the panel.
 */
@Component({
  selector: 'ws-workspace-switcher',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperIconComponent, TranslocoPipe],
  template: `
    @if (customers().length > 1) {
      <div class="ws ph-no-capture">
        <button
          class="row"
          type="button"
          data-testid="switch-workspace"
          [attr.aria-expanded]="listOpen()"
          (click)="toggleList()"
        >
          <paper-icon class="row__icon" name="building" [size]="16" aria-hidden="true" />
          <span class="row__text">{{ 'shell.profile_panel.switch_workspace' | transloco }}</span>
          @if (currentName(); as name) {
            <span class="row__value">{{ name }}</span>
          }
          <paper-icon class="row__go" [name]="listOpen() ? 'chevron-down' : 'chevron-right'" [size]="14" aria-hidden="true" />
        </button>

        @if (listOpen()) {
          <div class="list">
            <!-- Progressive disclosure: the filter appears only past the shared
                 threshold. ph-mask keeps the typed text out of session replay,
                 and onQueryChange keeps it out of analytics: a workspace list
                 is a list of customer names. A native field by design: it is
                 part of the panel's menu, the same documented exception as the
                 viewer overlays' search (CLAUDE.md), not a settings form. -->
            @if (showSearch()) {
              <div class="search">
                <paper-icon name="search" [size]="13" aria-hidden="true" />
                <input
                  type="text"
                  class="ph-mask"
                  [value]="query()"
                  (input)="onQueryChange($any($event.target).value)"
                  [placeholder]="'shell.app_shell.search_organisations' | transloco"
                  [attr.aria-label]="'shell.app_shell.search_organisations' | transloco"
                  autocomplete="off" />
              </div>
            }

            <div role="listbox" [attr.aria-label]="'shell.app_shell.organisations' | transloco">
              @for (c of filtered(); track c.id) {
                <button
                  class="row row--sub"
                  type="button"
                  role="option"
                  [attr.aria-selected]="c.id === currentId"
                  [disabled]="c.id === currentId || switchingId() !== null"
                  (click)="switchTo(c.id)"
                >
                  @if (c.logo && !logoErrors()[c.id]) {
                    <span class="logo-frame" aria-hidden="true">
                      <img class="logo" [src]="c.logo" alt="" loading="lazy" (error)="onLogoError(c.id)" />
                    </span>
                  }
                  <span class="row__text">{{ c.name }}</span>
                  @if (c.isTest) {
                    <span class="tag">{{ 'shell.app_shell.demo' | transloco }}</span>
                  }
                  @if (c.id === currentId) {
                    <paper-icon name="check" [size]="13" aria-hidden="true" />
                  } @else if (switchingId() === c.id) {
                    <span class="spinner" aria-hidden="true"></span>
                  }
                </button>
              }
            </div>

            @if (showSearch() && filtered().length === 0) {
              <div class="empty">{{ 'shell.app_shell.no_organisations_match' | transloco }}</div>
            }
            @if (customersService.switchError(); as switchError) {
              <div class="error" role="alert">
                <paper-icon name="alert-triangle" [size]="13" aria-hidden="true" />
                <span>{{ 'shell.app_shell.switch_failed' | transloco: { reason: switchError.message } }}</span>
              </div>
            }
          </div>
        }
      </div>
    }
  `,
  styles: `
    :host {
      display: block;
    }
    .ws {
      display: flex;
      flex-direction: column;
      gap: 1px;
      margin-top: 4px;
      padding-top: 8px;
      border-top: 1px solid var(--paper-hairline, var(--border-subtle));
    }
    .row {
      appearance: none;
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      min-height: 36px;
      padding: 8px;
      border: 0;
      border-radius: var(--radius-sm, 8px);
      background: transparent;
      color: var(--text-primary);
      font: inherit;
      font-size: var(--type-label-size, 12px);
      line-height: var(--type-label-leading, 16px);
      text-align: start;
      cursor: pointer;
    }
    .row:hover:not(:disabled) {
      background: var(--paper-profile-panel-row-bg-hover, var(--surface-hover));
    }
    .row:focus-visible {
      outline: var(--focus-ring, 2px solid var(--accent-focus, currentColor));
      outline-offset: -2px;
    }
    .row:disabled {
      cursor: default;
    }
    .row[aria-selected='true'] .row__text {
      font-weight: var(--fw-medium, 500);
    }
    .row--sub {
      padding-inline-start: 34px;
    }
    .row__icon,
    .row__go {
      display: inline-flex;
      flex: none;
      color: var(--text-secondary);
    }
    .row__text {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .row__value {
      max-width: 40%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: var(--text-secondary);
    }
    .list {
      display: flex;
      flex-direction: column;
      gap: 1px;
    }
    .search {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 2px 0 4px 34px;
      padding: 0 10px;
      height: 32px;
      border-radius: 18px;
      background: var(--surface-hover);
      color: var(--text-secondary);
    }
    .search:focus-within {
      outline: var(--focus-ring, 2px solid var(--accent-focus, currentColor));
      outline-offset: -2px;
    }
    .search input {
      flex: 1;
      min-width: 0;
      height: 100%;
      padding: 0;
      border: 0;
      outline: none;
      background: transparent;
      color: var(--text-primary);
      font: inherit;
      font-size: var(--type-label-size, 12px);
    }
    .search input::placeholder {
      color: var(--text-secondary);
      opacity: 1;
    }
    .empty,
    .error {
      padding: 8px 8px 8px 34px;
      font-size: var(--type-meta-size, 12px);
      color: var(--text-secondary);
    }
    .error {
      display: flex;
      align-items: flex-start;
      gap: 6px;
      color: var(--error-text, var(--text-primary));
    }
    /* The workspace's own mark, capped in BOTH axes: logos arrive at whatever
       ratio the customer uploaded. */
    .logo-frame {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: none;
      height: 16px;
      min-width: 16px;
      max-width: 52px;
    }
    .logo {
      height: 14px;
      width: auto;
      max-width: 52px;
      object-fit: contain;
    }
    /* Demo/test workspaces are labelled so an internal user always knows they
       are about to enter synthetic data. */
    .tag {
      flex: none;
      padding: 1px 7px;
      border: 1px solid var(--border-default);
      border-radius: var(--radius-pill);
      font-size: var(--type-meta-size, 12px);
      color: var(--text-secondary);
    }
    .spinner {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--text-secondary);
      animation: ws-workspace-pulse 1s ease infinite;
    }
    @keyframes ws-workspace-pulse {
      0%, 100% { opacity: 0.4; }
      50% { opacity: 1; }
    }
    @media (prefers-reduced-motion: reduce) {
      .spinner { animation: none; }
    }
  `,
})
export class WorkspaceSwitcherComponent {
  /** Whether the profile panel is open: the list loads then, and resets when it closes. */
  readonly panelOpen = input(false);

  protected readonly customersService = inject(PlatformCustomerService);
  private readonly analytics = inject(AnalyticsService);

  /** Accessible customers; the block only renders when there's more than one. */
  protected readonly customers = this.customersService.customers;
  /** The customer the session is scoped to (read once; null = none). */
  protected readonly currentId = this.customersService.currentCustomerId();

  private readonly _listOpen = signal(false);
  protected readonly listOpen = this._listOpen.asReadonly();
  private readonly _query = signal('');
  protected readonly query = this._query.asReadonly();
  private readonly _switchingId = signal<string | null>(null);
  protected readonly switchingId = this._switchingId.asReadonly();
  private readonly _logoErrors = signal<Record<string, boolean>>({});
  protected readonly logoErrors = this._logoErrors.asReadonly();

  protected readonly showSearch = computed(() =>
    shouldShowSearch(this.customers().length, SWITCHER_SEARCH_THRESHOLD),
  );
  protected readonly filtered = computed(() => filterCustomers(this.customers(), this._query()));
  protected readonly currentName = computed(
    () => this.customers().find((c) => c.id === this.currentId)?.name ?? null,
  );

  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Load on open — this is the ONLY surface that asks for the list — and
    // start from a collapsed, unfiltered list every time it opens.
    effect(() => {
      const open = this.panelOpen();
      untracked(() => {
        if (open) {
          this.customersService.clearSwitchError();
          void this.customersService.load();
        } else {
          this._listOpen.set(false);
          this._query.set('');
        }
      });
    });
    inject(DestroyRef).onDestroy(() => {
      if (this.searchTimer) clearTimeout(this.searchTimer);
    });
  }

  protected toggleList(): void {
    const next = !this._listOpen();
    this._listOpen.set(next);
    if (!next) this._query.set('');
  }

  protected onLogoError(customerId: string): void {
    this._logoErrors.update((m) => ({ ...m, [customerId]: true }));
  }

  /**
   * Update the filter, and schedule the debounced SEARCH event.
   *
   * THE TYPED TEXT NEVER LEAVES THE BROWSER. Workspace names are customer
   * names, so the query is somebody's client list. The event carries its
   * LENGTH and the two counts that make it analysable, and nothing else.
   */
  protected onQueryChange(value: string): void {
    this._query.set(value);
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      const len = this._query().trim().length;
      if (len > 0) {
        this.analytics.capture(ANALYTICS_EVENTS.ORG_SWITCHER_SEARCHED, {
          query_length: len,
          customer_count: this.customers().length,
          result_count: this.filtered().length,
        });
      }
    }, WORKSPACE_SEARCH_DEBOUNCE_MS);
  }

  protected async switchTo(id: string): Promise<void> {
    if (!id || id === this.currentId || this._switchingId()) return;
    this._switchingId.set(id);
    // Pre-reload cleanup (map prefs reset, customer-scoped storage, persisted
    // Apollo cache purge) is centralised inside switchTo().
    const ok = await this.customersService.switchTo(id, 'profile');
    // On success the app hard-reloads; only reset the spinner on failure.
    if (!ok) this._switchingId.set(null);
  }
}
